import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Bell,
  BellOff,
  ArrowLeft,
  Mail,
  MessageSquare,
  Briefcase,
  Video,
  Target,
  Users,
  Check,
  Loader2,
  Zap,
  TrendingUp,
  Calendar,
  Send,
  FileText,
  Download,
  Clock,
  AlarmClock,
  Globe,
  DollarSign,
  ChevronDown
} from "lucide-react";

// Currency Selector Component
const CurrencySelector = () => {
  const [currencies, setCurrencies] = useState([]);
  const [currentCurrency, setCurrentCurrency] = useState({ code: "USD", symbol: "$", name: "US Dollar" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchCurrencies();
    detectCurrency();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await fetch(`${API}/subscription/currencies`);
      if (response.ok) {
        const data = await response.json();
        setCurrencies(data.currencies || []);
      }
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
    }
  };

  const detectCurrency = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/detect-currency`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentCurrency({
          code: data.currency,
          symbol: data.symbol,
          name: data.name
        });
      }
    } catch (error) {
      console.error("Failed to detect currency:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyChange = async (currency) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/subscription/set-currency`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currency: currency.code })
      });
      
      if (response.ok) {
        setCurrentCurrency(currency);
        toast.success(`Currency changed to ${currency.name}`);
      } else {
        toast.error("Failed to update currency");
      }
    } catch (error) {
      toast.error("Failed to update currency");
    } finally {
      setSaving(false);
      setIsOpen(false);
    }
  };

  return (
    <motion.div
      className="bg-obsidian-light border border-[#222] p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center">
          <DollarSign className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-bold">Currency Preferences</h2>
          <p className="text-gray-400 text-sm">Choose your preferred currency for pricing</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-crimson" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between p-4 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
              data-testid="currency-dropdown"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{currentCurrency.symbol}</span>
                <div className="text-left">
                  <span className="text-white font-medium">{currentCurrency.code}</span>
                  <span className="text-gray-500 text-sm ml-2">{currentCurrency.name}</span>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-obsidian border border-[#333] shadow-xl z-50 max-h-64 overflow-y-auto">
                {currencies.map(currency => (
                  <button
                    key={currency.code}
                    onClick={() => handleCurrencyChange(currency)}
                    disabled={saving}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-crimson/10 transition-colors ${
                      currentCurrency.code === currency.code ? "bg-crimson/20" : ""
                    }`}
                    data-testid={`currency-option-${currency.code}`}
                  >
                    <span className="text-lg w-8">{currency.symbol}</span>
                    <div>
                      <span className="text-white">{currency.code}</span>
                      <span className="text-gray-500 text-sm ml-2">{currency.name}</span>
                    </div>
                    {currentCurrency.code === currency.code && (
                      <Check className="w-4 h-4 text-crimson ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <p className="text-gray-500 text-sm">
            This will be used to display pricing throughout the app. Your payment method's currency may differ.
          </p>
        </div>
      )}
    </motion.div>
  );
};

// Interview Reminders Component
const InterviewReminders = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    fetchPrefs();
    fetchUpcoming();
  }, []);

  const fetchPrefs = async () => {
    try {
      const response = await fetch(`${API}/reminders/preferences`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPrefs(data);
      }
    } catch (error) {
      console.error("Failed to fetch reminder preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const response = await fetch(`${API}/reminders/upcoming`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUpcoming(data);
      }
    } catch (error) {
      console.error("Failed to fetch upcoming reminders:", error);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/reminders/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(prefs),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrefs(data);
        toast.success("Reminder preferences saved!");
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-crimson animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4 pt-4 border-t border-[#333]">
      {/* Enable Toggle */}
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-gray-300">Enable interview reminders</span>
        <button
          onClick={() => setPrefs(prev => ({ ...prev, enabled: !prev?.enabled }))}
          className={`w-12 h-6 rounded-full transition-colors ${
            prefs?.enabled ? "bg-crimson" : "bg-gray-700"
          }`}
          data-testid="reminder-toggle"
        >
          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
            prefs?.enabled ? "translate-x-6" : "translate-x-0.5"
          }`} />
        </button>
      </label>

      {prefs?.enabled && (
        <>
          {/* Reminder Timing */}
          <div className="space-y-3">
            <label className="text-sm text-gray-400 block">Remind me:</label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.reminder_24h !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, reminder_24h: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="reminder-24h"
              />
              <span className="text-gray-300">24 hours before</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.reminder_1h !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, reminder_1h: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="reminder-1h"
              />
              <span className="text-gray-300">1 hour before</span>
            </label>
          </div>

          {/* Delivery Methods */}
          <div className="space-y-3">
            <label className="text-sm text-gray-400 block">Delivery method:</label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.email_reminders !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, email_reminders: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="reminder-email"
              />
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">Email</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.push_reminders !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, push_reminders: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="reminder-push"
              />
              <Bell className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">Push Notification</span>
            </label>
          </div>

          {/* Upcoming Interviews */}
          {upcoming.length > 0 && (
            <div className="p-3 bg-obsidian border border-[#333]">
              <p className="text-sm text-gray-400 mb-2">Upcoming interviews (next 24h):</p>
              <div className="space-y-2">
                {upcoming.map((interview, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-white">{interview.job_title || "Interview"}</span>
                    <span className="text-crimson">{interview.hours_until}h away</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={savePrefs}
            disabled={saving}
            className="bg-crimson hover:bg-crimson-hover"
            data-testid="save-reminder-prefs-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </>
      )}
    </div>
  );
};

// Scheduled PDF Reports Component
const ScheduledPDFReports = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  useEffect(() => {
    fetchPrefs();
  }, []);

  const fetchPrefs = async () => {
    try {
      const response = await fetch(`${API}/digests/pdf-schedule`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPrefs(data);
      }
    } catch (error) {
      console.error("Failed to fetch PDF schedule preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/digests/pdf-schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(prefs),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrefs(data);
        toast.success("PDF schedule saved!");
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const response = await fetch(`${API}/digests/pdf-schedule/send-now`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`PDF reports sent to your email! Reports: ${data.reports.join(", ")}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reports");
      }
    } catch (error) {
      toast.error("Failed to send reports");
    } finally {
      setSendingNow(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-crimson animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4 pt-4 border-t border-[#333]">
      {/* Enable Toggle */}
      <label className="flex items-center justify-between cursor-pointer">
        <span className="text-gray-300">Enable scheduled PDF reports</span>
        <button
          onClick={() => setPrefs(prev => ({ ...prev, enabled: !prev?.enabled }))}
          className={`w-12 h-6 rounded-full transition-colors ${
            prefs?.enabled ? "bg-crimson" : "bg-gray-700"
          }`}
          data-testid="pdf-schedule-toggle"
        >
          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
            prefs?.enabled ? "translate-x-6" : "translate-x-0.5"
          }`} />
        </button>
      </label>

      {prefs?.enabled && (
        <>
          {/* Frequency */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Frequency</label>
            <div className="flex gap-2">
              {["weekly", "monthly"].map((freq) => (
                <button
                  key={freq}
                  onClick={() => setPrefs(prev => ({ ...prev, frequency: freq }))}
                  className={`px-4 py-2 text-sm capitalize transition-colors ${
                    prefs?.frequency === freq
                      ? "bg-crimson text-white"
                      : "bg-obsidian border border-[#333] text-gray-400 hover:text-white"
                  }`}
                  data-testid={`pdf-freq-${freq}`}
                >
                  {freq}
                </button>
              ))}
            </div>
          </div>

          {/* Report Types */}
          <div className="space-y-3">
            <label className="text-sm text-gray-400 block">Include reports:</label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.include_market_report !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, include_market_report: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="pdf-include-market"
              />
              <span className="text-gray-300">Market Insights Report</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={prefs?.include_personal_report !== false}
                onChange={(e) => setPrefs(prev => ({ ...prev, include_personal_report: e.target.checked }))}
                className="w-4 h-4 accent-crimson"
                data-testid="pdf-include-personal"
              />
              <span className="text-gray-300">Personal Analytics Report</span>
            </label>
          </div>

          {/* Next Scheduled */}
          {prefs?.next_scheduled_at && (
            <div className="p-3 bg-obsidian border border-[#333]">
              <p className="text-sm text-gray-400">Next report scheduled:</p>
              <p className="text-white font-medium">
                {new Date(prefs.next_scheduled_at).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={savePrefs}
              disabled={saving}
              className="bg-crimson hover:bg-crimson-hover"
              data-testid="save-pdf-schedule-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Schedule
            </Button>
            <Button
              onClick={sendNow}
              disabled={sendingNow}
              variant="outline"
              className="border-crimson text-crimson hover:bg-crimson/10"
              data-testid="send-pdf-now-btn"
            >
              {sendingNow ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Send Now
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const NotificationSettings = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [digestPrefs, setDigestPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");

  useEffect(() => {
    // Check if push notifications are supported
    if ("Notification" in window && "serviceWorker" in navigator) {
      setPushSupported(true);
      setPushPermission(Notification.permission);
    }
    fetchPreferences();
    fetchDigestPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API}/notifications/preferences`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDigestPreferences = async () => {
    try {
      const response = await fetch(`${API}/digests/preferences`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDigestPrefs(data);
      }
    } catch (error) {
      console.error("Failed to fetch digest preferences:", error);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      // Save notification preferences
      const response = await fetch(`${API}/notifications/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(preferences),
      });
      
      // Save digest preferences
      if (digestPrefs) {
        await fetch(`${API}/digests/preferences`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(digestPrefs),
        });
      }
      
      if (response.ok) {
        toast.success("Preferences saved!");
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const response = await fetch(`${API}/digests/send-test`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Test digest sent to your email!");
      } else {
        toast.error("Failed to send test digest");
      }
    } catch (error) {
      toast.error("Failed to send test digest");
    } finally {
      setSendingTest(false);
    }
  };

  const requestPushPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission === "granted") {
        // Subscribe to push notifications
        const registration = await navigator.serviceWorker.ready;
        
        // Get VAPID public key
        const keyResponse = await fetch(`${API}/notifications/vapid-public-key`, {
          credentials: "include",
        });
        const keyData = await keyResponse.json();
        
        if (keyData.key) {
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(keyData.key),
          });
          
          // Send subscription to server
          await fetch(`${API}/notifications/subscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ subscription: subscription.toJSON() }),
          });
          
          toast.success("Push notifications enabled!");
        } else {
          toast.info("Push notifications not configured on server");
        }
      } else if (permission === "denied") {
        toast.error("Push notifications were denied");
      }
    } catch (error) {
      console.error("Push subscription failed:", error);
      toast.error("Failed to enable push notifications");
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const toggleCategory = (category) => {
    setPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  };

  const dashboardUrl = user?.role === "recruiter" ? "/dashboard/recruiter" : "/dashboard/jobseeker";

  const categories = [
    { key: "messages", icon: MessageSquare, label: "Messages", description: "New messages and replies" },
    { key: "applications", icon: Briefcase, label: "Applications", description: "Application updates and status changes" },
    { key: "interviews", icon: Video, label: "Interviews", description: "Interview invitations and reminders" },
    { key: "job_matches", icon: Target, label: "Job Matches", description: "New jobs matching your profile" },
    { key: "community", icon: Users, label: "Community", description: "Likes, comments, and mentions" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-6 pt-16 lg:pt-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                <span className="text-crimson">NOTIFICATIONS</span>
              </h1>
              <p className="text-gray-400">Manage your notification preferences</p>
            </div>
            <Button
              onClick={savePreferences}
              disabled={saving}
              className="bg-crimson hover:bg-crimson-hover text-white"
              data-testid="save-preferences-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>

          <div className="space-y-8">
            {/* Push Notifications */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Push Notifications</h2>
                  <p className="text-gray-400 text-sm">Receive notifications even when not on Rekrutor</p>
                </div>
              </div>
              {pushSupported && (
                <div className="flex items-center gap-3">
                  {pushPermission === "granted" ? (
                    <span className="flex items-center gap-2 text-green-400 text-sm">
                      <Check className="w-4 h-4" /> Enabled
                    </span>
                  ) : pushPermission === "denied" ? (
                    <span className="text-red-400 text-sm">Blocked in browser</span>
                  ) : (
                    <Button
                      onClick={requestPushPermission}
                      variant="outline"
                      className="border-crimson text-crimson hover:bg-crimson/10"
                      data-testid="enable-push-btn"
                    >
                      Enable
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {!pushSupported && (
              <p className="text-yellow-500/80 text-sm">
                Push notifications are not supported in your browser.
              </p>
            )}

            <div className="mt-4 pt-4 border-t border-[#333]">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300">Enable push notifications</span>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, push_enabled: !prev.push_enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences?.push_enabled ? "bg-crimson" : "bg-gray-700"
                  }`}
                  data-testid="push-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences?.push_enabled ? "translate-x-6" : "translate-x-0.5"
                  }`} />
                </button>
              </label>
            </div>
          </motion.div>

          {/* Email Notifications */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Email Notifications</h2>
                  <p className="text-gray-400 text-sm">Receive updates via email</p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-[#333]">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300">Enable email notifications</span>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, email_enabled: !prev.email_enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences?.email_enabled ? "bg-crimson" : "bg-gray-700"
                  }`}
                  data-testid="email-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences?.email_enabled ? "translate-x-6" : "translate-x-0.5"
                  }`} />
                </button>
              </label>
            </div>
          </motion.div>

          {/* Notification Categories */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-heading text-lg font-bold mb-4">Notification Types</h2>
            <p className="text-gray-400 text-sm mb-6">Choose which notifications you want to receive</p>
            
            <div className="space-y-4">
              {categories.map((category) => (
                <div
                  key={category.key}
                  className="flex items-center justify-between p-4 bg-obsidian border border-[#333] hover:border-[#444] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-crimson/10 flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-crimson" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{category.label}</h3>
                      <p className="text-gray-500 text-sm">{category.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleCategory(category.key)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      preferences?.categories?.[category.key] ? "bg-crimson" : "bg-gray-700"
                    }`}
                    data-testid={`toggle-${category.key}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      preferences?.categories?.[category.key] ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Application Status Email Preferences (Job Seekers Only) */}
          {user?.role === "jobseeker" && (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Application Status Emails</h2>
                  <p className="text-gray-400 text-sm">Choose which status changes trigger email notifications</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-[#333] space-y-4">
                <p className="text-sm text-gray-500 mb-4">
                  Receive emails when your application status changes to:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: "email_on_reviewed", label: "Reviewed", icon: "👀", color: "blue", desc: "When recruiter views your application" },
                    { key: "email_on_shortlisted", label: "Shortlisted", icon: "⭐", color: "green", desc: "When you're shortlisted for a position" },
                    { key: "email_on_interviewed", label: "Interviewed", icon: "🎥", color: "purple", desc: "After completing an interview" },
                    { key: "email_on_offered", label: "Offered", icon: "🎉", color: "emerald", desc: "When you receive a job offer" },
                    { key: "email_on_hired", label: "Hired", icon: "🎊", color: "green", desc: "When your hiring is confirmed" },
                    { key: "email_on_rejected", label: "Not Selected", icon: "📋", color: "red", desc: "When application is closed" },
                  ].map((status) => (
                    <label 
                      key={status.key}
                      className={`flex items-center gap-3 p-3 bg-obsidian border border-[#333] cursor-pointer hover:border-${status.color}-500/50 transition-colors`}
                    >
                      <input
                        type="checkbox"
                        checked={preferences?.status_emails?.[status.key] !== false}
                        onChange={(e) => setPreferences(prev => ({
                          ...prev,
                          status_emails: {
                            ...prev.status_emails,
                            [status.key]: e.target.checked
                          }
                        }))}
                        className="w-4 h-4 accent-crimson"
                        data-testid={`status-${status.key}`}
                      />
                      <span className="text-lg">{status.icon}</span>
                      <div className="flex-1">
                        <span className="text-white font-medium block">{status.label}</span>
                        <span className="text-gray-500 text-xs">{status.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-obsidian border border-[#333]">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-white font-medium">Only notify on major updates</span>
                      <p className="text-gray-500 text-xs mt-1">
                        Only receive emails for Shortlisted, Offered, and Hired statuses
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setPreferences(prev => ({
                          ...prev,
                          status_emails: {
                            email_on_reviewed: false,
                            email_on_shortlisted: true,
                            email_on_interviewed: false,
                            email_on_offered: true,
                            email_on_hired: true,
                            email_on_rejected: false,
                          }
                        }));
                      }}
                      className="px-3 py-1 text-sm bg-crimson/20 border border-crimson/30 text-crimson hover:bg-crimson/30"
                      data-testid="major-updates-only-btn"
                    >
                      Apply
                    </button>
                  </label>
                </div>
              </div>
            </motion.div>
          )}

          {/* Currency Preferences */}
          <CurrencySelector />

          {/* Email Digests */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Career Digest</h2>
                  <p className="text-gray-400 text-sm">Receive personalized career insights and market trends via email</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 mt-4 pt-4 border-t border-[#333]">
              {/* Enable Toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-gray-300">Enable career digest emails</span>
                <button
                  onClick={() => setDigestPrefs(prev => ({ ...prev, enabled: !prev?.enabled }))}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    digestPrefs?.enabled ? "bg-crimson" : "bg-gray-700"
                  }`}
                  data-testid="digest-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    digestPrefs?.enabled ? "translate-x-6" : "translate-x-0.5"
                  }`} />
                </button>
              </label>

              {/* Frequency Selection */}
              {digestPrefs?.enabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Frequency */}
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Frequency</label>
                      <div className="flex gap-2">
                        {["daily", "weekly", "monthly"].map((freq) => (
                          <button
                            key={freq}
                            onClick={() => setDigestPrefs(prev => ({ ...prev, frequency: freq }))}
                            className={`px-4 py-2 text-sm capitalize transition-colors ${
                              digestPrefs?.frequency === freq
                                ? "bg-crimson text-white"
                                : "bg-obsidian border border-[#333] text-gray-400 hover:text-white"
                            }`}
                            data-testid={`digest-freq-${freq}`}
                          >
                            {freq}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Preferred Time */}
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Delivery Time</label>
                      <select
                        value={digestPrefs?.preferred_time || "09:00"}
                        onChange={(e) => setDigestPrefs(prev => ({ ...prev, preferred_time: e.target.value }))}
                        className="w-full bg-obsidian border border-[#333] text-white py-2 px-3 focus:border-crimson outline-none"
                        data-testid="digest-preferred-time"
                      >
                        {["06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00", 
                          "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"].map((time) => (
                          <option key={time} value={time}>
                            {time} ({parseInt(time) < 12 ? `${time} AM` : parseInt(time) === 12 ? '12:00 PM' : `${parseInt(time) - 12}:00 PM`})
                          </option>
                        ))}
                      </select>
                      <p className="text-gray-500 text-xs mt-1">Time in UTC timezone</p>
                    </div>
                  </div>

                  {/* Content Options */}
                  <div className="space-y-3">
                    <label className="text-sm text-gray-400 block">Market Insights:</label>
                    {[
                      { key: "include_trending_skills", label: "Trending Skills" },
                      { key: "include_salary_insights", label: "Salary Insights" },
                      { key: "include_industry_breakdown", label: "Industry Breakdown" },
                      { key: "include_market_trends", label: "Market Trends" },
                    ].map((option) => (
                      <label key={option.key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={digestPrefs?.[option.key] !== false}
                          onChange={(e) => setDigestPrefs(prev => ({ ...prev, [option.key]: e.target.checked }))}
                          className="w-4 h-4 accent-crimson"
                          data-testid={`digest-${option.key}`}
                        />
                        <span className="text-gray-300">{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Personalized Content Options */}
                  <div className="space-y-3 mt-4 pt-4 border-t border-[#333]">
                    <label className="text-sm text-gray-400 block">Personalized Content:</label>
                    {[
                      { key: "include_job_recommendations", label: "Job Recommendations", desc: "Personalized job matches based on your profile" },
                      { key: "include_application_updates", label: "Application Updates", desc: "Status changes on your job applications" },
                      { key: "include_interview_reminders", label: "Interview Reminders", desc: "Upcoming scheduled interviews" },
                      { key: "include_community_highlights", label: "Community Highlights", desc: "Top discussions and posts" },
                      { key: "include_profile_tips", label: "Profile Tips", desc: "Suggestions to improve your profile" },
                    ].map((option) => (
                      <label key={option.key} className="flex items-start gap-3 cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={digestPrefs?.[option.key] !== false}
                          onChange={(e) => setDigestPrefs(prev => ({ ...prev, [option.key]: e.target.checked }))}
                          className="w-4 h-4 accent-crimson mt-0.5"
                          data-testid={`digest-${option.key}`}
                        />
                        <div>
                          <span className="text-gray-300">{option.label}</span>
                          <p className="text-gray-500 text-xs">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Actions Row */}
                  <div className="flex gap-3 pt-4 border-t border-[#333]">
                    <Button
                      onClick={sendTestDigest}
                      disabled={sendingTest}
                      variant="outline"
                      className="border-crimson text-crimson hover:bg-crimson/10"
                      data-testid="send-test-digest-btn"
                    >
                      {sendingTest ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Test Digest
                    </Button>
                    <Button
                      onClick={() => window.open(`${API}/digests/preview`, '_blank')}
                      variant="outline"
                      className="border-[#333] text-gray-300 hover:text-white hover:border-gray-500"
                      data-testid="preview-digest-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Preview Content
                    </Button>
                  </div>
                  
                  {/* Last Sent Info */}
                  {digestPrefs?.last_sent_at && (
                    <div className="text-gray-500 text-sm pt-2">
                      Last digest sent: {new Date(digestPrefs.last_sent_at).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>

          {/* Scheduled PDF Reports */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Scheduled PDF Reports</h2>
                  <p className="text-gray-400 text-sm">Receive analytics reports as PDF via email</p>
                </div>
              </div>
            </div>
            
            <ScheduledPDFReports />
          </motion.div>

          {/* Interview Reminders */}
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AlarmClock className="w-6 h-6 text-crimson" />
                <div>
                  <h2 className="font-heading text-lg font-bold">Interview Reminders</h2>
                  <p className="text-gray-400 text-sm">Get notified before scheduled interviews</p>
                </div>
              </div>
            </div>
            
            <InterviewReminders />
          </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationSettings;
